/**
 * Parse a Nominatim (OpenStreetMap) autocomplete result into normalised
 * address fields used across our address forms.
 *
 * Why this helper exists:
 * Nominatim's structured `address` object often *omits* `house_number` even
 * when the address obviously has one — the number ends up either:
 *   - prefixed onto `road` ("2683 Lawrence Ave E"), or
 *   - present only at the start of `display_name`.
 * Reading `house_number` alone leaves the Street No. field blank for many
 * Canadian addresses. This helper falls back through all three sources and
 * also handles unit/suite prefixes (e.g. "Unit 5 - 22 Finch Ave").
 */

export type NominatimAddress = {
  house_number?: string;
  road?: string;
  city?: string;
  town?: string;
  village?: string;
  hamlet?: string;
  suburb?: string;
  neighbourhood?: string;
  state?: string;
  postcode?: string;
  country?: string;
  country_code?: string;
};

export type NominatimResult = {
  display_name: string;
  address: NominatimAddress;
};

export type ParsedAddress = {
  streetNumber: string;
  streetName: string;
  city: string;
  province: string;
  postalCode: string;
  country: string;
};

// Matches a leading street number, optionally with a letter suffix (100A),
// hyphenated suite ranges (12-345), or a "Unit N -" prefix (Unit 5 - 22).
const LEADING_NUMBER_RE =
  /^(?:(?:unit|suite|apt|apartment)\s+[\w-]+\s*[-,]\s*)?(\d+(?:[A-Za-z]|-\d+)?)\b/i;

const extractLeadingNumber = (s: string | undefined | null): string => {
  if (!s) return "";
  const match = s.trim().match(LEADING_NUMBER_RE);
  return match ? match[1] : "";
};

const stripLeadingNumber = (s: string | undefined | null): string => {
  if (!s) return "";
  return s.trim().replace(LEADING_NUMBER_RE, "").trim();
};

/**
 * Repair an existing (number, name) pair where the number was previously
 * saved glued onto the name, e.g. ("", "2683 Lawrence Ave E"). Used to
 * clean up legacy records on edit-mode prefill.
 *
 * Leaves correctly-saved data untouched (already has a number, or the name
 * doesn't begin with one).
 */
export function splitStreetNumberFromName(
  rawNumber: string | undefined | null,
  rawName: string | undefined | null,
): { streetNumber: string; streetName: string } {
  const number = (rawNumber || "").trim();
  const name = (rawName || "").trim();
  if (number || !name) return { streetNumber: number, streetName: name };
  const extracted = extractLeadingNumber(name);
  if (!extracted) return { streetNumber: "", streetName: name };
  return { streetNumber: extracted, streetName: stripLeadingNumber(name) };
}

export function parseNominatimResult(result: NominatimResult): ParsedAddress {
  const a = result.address || {};
  let streetNumber = (a.house_number || "").trim();
  let streetName = (a.road || "").trim();

  // Fallback 1: street number is glued onto `road` (e.g. "2683 Lawrence Ave E")
  if (!streetNumber && streetName) {
    const fromRoad = extractLeadingNumber(streetName);
    if (fromRoad) {
      streetNumber = fromRoad;
      streetName = stripLeadingNumber(streetName);
    }
  }

  // Fallback 2: pull from the very first segment of display_name
  if (!streetNumber && result.display_name) {
    const firstSegment = result.display_name.split(",")[0] || "";
    const fromDisplay = extractLeadingNumber(firstSegment);
    if (fromDisplay) {
      streetNumber = fromDisplay;
      // If we don't yet have a street name, derive it from the segment too.
      if (!streetName) streetName = stripLeadingNumber(firstSegment);
    }
  }

  return {
    streetNumber,
    streetName,
    city: (a.city || a.town || a.village || a.hamlet || a.suburb || a.neighbourhood || "").trim(),
    province: (a.state || "").trim(),
    postalCode: (a.postcode || "").trim(),
    country: (a.country || "").trim(),
  };
}
